/**
 * CSRF Token Management Utilities
 *
 * Handles CSRF token retrieval, storage, and validation for secure API requests.
 *
 * CRITICAL SECURITY:
 * ────────────────────────────────────────────────────────────────────────────
 * All state-changing requests (POST/PATCH/DELETE) MUST include a CSRF token
 * in the X-CSRF-Token header. This prevents Cross-Site Request Forgery attacks.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * USAGE:
 * 1. Call fetchCsrfToken() on app initialization
 * 2. Token is automatically stored in memory
 * 3. Use getCsrfToken() to retrieve token for API requests
 * 4. Token is also available in csrf_token cookie (double-submit pattern)
 *
 * WORKFLOW:
 * - App loads → fetchCsrfToken() → Token stored in memory + cookie
 * - User makes POST request → getCsrfToken() → Include in X-CSRF-Token header
 * - Backend validates → Header matches cookie → Request allowed
 *
 * @module csrf
 */

import { ENV } from '../config/env';

const API_BASE_URL = ENV.apiUrl;

/**
 * In-memory CSRF token storage
 * Token is also stored in httpOnly csrf_token cookie by backend
 */
let csrfToken: string | null = null;

/**
 * Token expiration timestamp (24 hours from fetch)
 */
let tokenExpiry: number | null = null;

/**
 * Fetch CSRF token from backend
 *
 * Retrieves a fresh CSRF token from the backend and stores it in memory.
 * The backend also sets a csrf_token cookie for double-submit pattern validation.
 *
 * @returns Promise<string> CSRF token
 * @throws Error if token fetch fails
 *
 * @example
 * ```typescript
 * // Call on app initialization
 * await fetchCsrfToken();
 *
 * // Token is now available for use
 * const token = getCsrfToken();
 * ```
 */
export async function fetchCsrfToken(): Promise<string> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/csrf-token`, {
      method: 'GET',
      credentials: 'include', // Include cookies in request
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.statusText}`);
    }

    const data = await response.json();

    if (!data.success || !data.csrf_token) {
      throw new Error('Invalid CSRF token response');
    }

    // Store token in memory
    csrfToken = data.csrf_token;

    // Set expiration (24 hours from now)
    tokenExpiry = Date.now() + (data.expires_in * 1000);

    console.log('✅ CSRF token fetched successfully');

    // Ensure token was set before returning
    if (!csrfToken) {
      throw new Error('CSRF token was not set after successful fetch');
    }

    return csrfToken;
  } catch (error) {
    console.error('❌ Failed to fetch CSRF token:', error);
    throw error;
  }
}

/**
 * Get current CSRF token from memory
 *
 * Retrieves the CSRF token from memory. If token is expired or missing,
 * automatically fetches a new one.
 *
 * @returns Promise<string> CSRF token
 * @throws Error if token is unavailable and fetch fails
 *
 * @example
 * ```typescript
 * const token = await getCsrfToken();
 *
 * // Use in API request
 * await fetch('/api/v1/customers', {
 *   method: 'POST',
 *   headers: {
 *     'X-CSRF-Token': token,
 *     'Content-Type': 'application/json'
 *   },
 *   body: JSON.stringify(data)
 * });
 * ```
 */
export async function getCsrfToken(): Promise<string> {
  // Check if token exists and is not expired
  if (csrfToken && tokenExpiry && Date.now() < tokenExpiry) {
    return csrfToken;
  }

  // Token is missing or expired, fetch new one
  console.log('🔄 CSRF token expired or missing, fetching new token...');
  return await fetchCsrfToken();
}

/**
 * Check if CSRF token is valid (exists and not expired)
 *
 * @returns boolean True if token is valid
 *
 * @example
 * ```typescript
 * if (!isCsrfTokenValid()) {
 *   await fetchCsrfToken();
 * }
 * ```
 */
export function isCsrfTokenValid(): boolean {
  return csrfToken !== null && tokenExpiry !== null && Date.now() < tokenExpiry;
}

/**
 * Refresh CSRF token
 *
 * Forces a fetch of a new CSRF token, even if current token is valid.
 * Useful after authentication state changes.
 *
 * @returns Promise<string> New CSRF token
 *
 * @example
 * ```typescript
 * // After login
 * await refreshCsrfToken();
 *
 * // After logout
 * await refreshCsrfToken();
 * ```
 */
export async function refreshCsrfToken(): Promise<string> {
  console.log('🔄 Refreshing CSRF token...');
  csrfToken = null;
  tokenExpiry = null;
  return await fetchCsrfToken();
}

/**
 * Clear CSRF token from memory
 *
 * Clears the stored CSRF token. Useful during logout.
 *
 * @example
 * ```typescript
 * // On logout
 * clearCsrfToken();
 * ```
 */
export function clearCsrfToken(): void {
  csrfToken = null;
  tokenExpiry = null;
  console.log('🗑️ CSRF token cleared');
}

/**
 * Get CSRF headers for API requests
 *
 * Returns headers object with CSRF token for including in fetch requests.
 *
 * @returns Promise<Record<string, string>> Headers object with X-CSRF-Token
 * @throws Error if token is unavailable
 *
 * @example
 * ```typescript
 * const headers = await getCsrfHeaders();
 *
 * await fetch('/api/v1/customers', {
 *   method: 'POST',
 *   headers: {
 *     ...headers,
 *     'Content-Type': 'application/json'
 *   },
 *   body: JSON.stringify(data)
 * });
 * ```
 */
export async function getCsrfHeaders(): Promise<Record<string, string>> {
  const token = await getCsrfToken();
  return {
    'X-CSRF-Token': token,
  };
}

/**
 * Initialize CSRF protection
 *
 * Call this function when the application starts to fetch and store
 * the initial CSRF token.
 *
 * @returns Promise<void>
 *
 * @example
 * ```typescript
 * // In App.tsx or main.tsx
 * useEffect(() => {
 *   initializeCsrf();
 * }, []);
 * ```
 */
export async function initializeCsrf(): Promise<void> {
  try {
    await fetchCsrfToken();
    console.log('✅ CSRF protection initialized');
  } catch (error) {
    console.error('❌ Failed to initialize CSRF protection:', error);
    // Don't throw - app should still work for read-only operations
  }
}

/**
 * Check CSRF token status from backend
 *
 * Queries the backend to verify current CSRF token status.
 * Useful for debugging CSRF issues.
 *
 * @returns Promise<object> CSRF status information
 *
 * @example
 * ```typescript
 * const status = await checkCsrfStatus();
 * console.log('CSRF Status:', status);
 * ```
 */
export async function checkCsrfStatus(): Promise<{
  success: boolean;
  has_token: boolean;
  token_valid: boolean;
  authenticated: boolean;
  message: string;
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/csrf-status`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to check CSRF status: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Failed to check CSRF status:', error);
    throw error;
  }
}
